import i18next from "i18next";
import { isValidTimezone } from "./cron";

/**
 * UTC, and it is the default deliberately.
 *
 * The create form defaulted to the browser's own zone, which reads as the
 * friendlier choice and was the wrong one for two reasons.
 *
 * It disagreed with the rest of the platform. `createScheduleSchema` defaults
 * `timezone` to UTC and so does the `app_schedules` column, so the same job
 * created from the dashboard and from CI got schedules hours apart for
 * identical input -- a difference nothing on either side would have explained.
 * A server has no browser zone to copy, so the two could only be reconciled in
 * the browser.
 *
 * And UTC is the one that does not move. A cron in a zone with daylight saving
 * shifts against every other clock twice a year, which is not what somebody who
 * set "03:00 nightly" a year ago expects to have happen to it.
 *
 * The browser's zone stays first-class -- offered at the top, named as theirs,
 * one click -- because "03:00 my time" is a perfectly good thing to want. It is
 * just not the thing to assume.
 */
export const DEFAULT_TIMEZONE = "UTC";

/**
 * The zone this browser is in, as the browser reports it.
 *
 * Kept as its canonical id rather than mapped: a browser in India reports
 * `Asia/Calcutta`, which is the id every runtime knows and the one the list
 * below carries. Only its label is renamed.
 */
export function localTimezone(): string | null {
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return local && isValidTimezone(local) && local !== DEFAULT_TIMEZONE ? local : null;
}

/**
 * The fallback list, for a runtime with no `Intl.supportedValuesOf`.
 *
 * Deliberately a spread across the inhabited offsets rather than the zones this
 * team happens to work in: somebody on an old browser in Lagos or São Paulo
 * should still find something honest, even if not their own city.
 */
const FALLBACK_ZONES = [
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "America/Bogota",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/New_York",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Jakarta",
  "Asia/Kolkata",
  "Asia/Manila",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Melbourne",
  "Australia/Sydney",
  "Europe/Berlin",
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Moscow",
  "Europe/Paris",
  "Pacific/Auckland",
];

/**
 * Zones whose canonical id is a name the city no longer goes by.
 *
 * `Intl.supportedValuesOf` returns *canonical* IANA ids, and IANA keeps the
 * original spelling as canonical long after a place is renamed -- so the list a
 * runtime hands back today still says Kiev, Calcutta, Saigon and Katmandu. On a
 * platform sold everywhere that is not a cosmetic problem: those are the names
 * customers in those places stopped using, and one of them is a name people
 * have strong feelings about being shown.
 *
 * **This renames the label and never the stored id**, and that distinction was
 * learned the hard way. Substituting the id looks tidier and is unsafe: the
 * value is validated by `Intl` on the Worker, not in the browser, and the two
 * runtimes do not know the same set. workerd accepts `Europe/Kyiv` and
 * `Asia/Kolkata` but **rejects** `Europe/Uzhhorod` and `Europe/Zaporizhzhia`,
 * both of which Chrome and Node accept -- so a substituted id would have
 * rendered an option the browser thought was fine and the API answered 400 for.
 *
 * Storing the canonical id cannot have that problem: it is the id the runtime
 * itself listed, so anything that can list it can validate it. The customer
 * sees the modern name either way.
 */
const RENAMED: Record<string, string> = {
  "Africa/Asmera": "Asmara",
  "America/Buenos_Aires": "Buenos Aires",
  "America/Godthab": "Nuuk",
  "Asia/Calcutta": "Kolkata",
  "Asia/Katmandu": "Kathmandu",
  "Asia/Rangoon": "Yangon",
  "Asia/Saigon": "Ho Chi Minh",
  "Atlantic/Faeroe": "Faroe",
  "Europe/Kiev": "Kyiv",
  "Europe/Uzhgorod": "Uzhhorod",
  "Europe/Zaporozhye": "Zaporizhzhia",
};

/** The name a place currently goes by, where the canonical id lags. */
export function preferredName(zone: string): string {
  return RENAMED[zone] ?? "";
}

/**
 * Every zone this runtime knows, or a spread of them if it will not say.
 *
 * `Intl.supportedValuesOf` is the whole IANA database -- around 400 entries --
 * and it is the right source precisely because this platform is sold
 * everywhere. A curated shortlist is a list of the places its authors happen to
 * live, and a customer in Lagos or Santiago reading it learns that.
 *
 * Computed once. The set does not change while a tab is open, and building it
 * costs a few hundred `Intl` constructions for the offsets below.
 */
let cachedZones: string[] | null = null;

export function allTimezones(): string[] {
  if (cachedZones) return cachedZones;

  const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;

  let zones: string[];
  try {
    zones = typeof supportedValuesOf === "function" ? supportedValuesOf("timeZone") : FALLBACK_ZONES;
  } catch {
    zones = FALLBACK_ZONES;
  }

  // Filtered rather than trusted: a zone this runtime names and cannot format
  // would render an option that fails validation on save, which is worse than
  // one that is not there. Ids are left exactly as the runtime gave them --
  // see RENAMED for why substituting them was unsafe.
  cachedZones = [...new Set(zones.filter(isValidTimezone))].sort();
  return cachedZones;
}

/**
 * A zone's current offset from UTC, in minutes.
 *
 * Read out of `Intl` rather than from a table, so it is right across daylight
 * saving without this file knowing any of the rules.
 */
export function offsetMinutes(zone: string, at: Date = new Date()): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const found: Record<string, string> = {};
  for (const part of formatter.formatToParts(at)) found[part.type] = part.value;

  const asUtc = Date.UTC(
    Number(found.year),
    Number(found.month) - 1,
    Number(found.day),
    // `24` at midnight is an hourCycle quirk rather than a real hour.
    Number(found.hour) % 24,
    Number(found.minute),
    Number(found.second),
  );

  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** `UTC`, `UTC+07:00`, `UTC−03:30`. A true minus sign, not a hyphen. */
export function offsetLabel(zone: string, at: Date = new Date()): string {
  const minutes = offsetMinutes(zone, at);
  if (minutes === 0) return "UTC";
  const sign = minutes < 0 ? "−" : "+";
  const absolute = Math.abs(minutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const rest = String(absolute % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${rest}`;
}

export interface TimezoneOption {
  value: string;
  label: string;
}

export interface TimezoneGroup {
  label: string;
  options: TimezoneOption[];
}

/**
 * `Asia/Kuala_Lumpur` reads as `Kuala Lumpur`; the region is the group.
 *
 * A renamed place is shown under the name it goes by now, while its id stays
 * whatever the runtime listed.
 */
function cityOf(zone: string): string {
  const renamed = preferredName(zone);
  if (renamed) return renamed;
  const city = zone.includes("/") ? zone.slice(zone.indexOf("/") + 1) : zone;
  return city.replace(/_/g, " ").replace(/\//g, " — ");
}

function regionOf(zone: string): string {
  return zone.includes("/") ? zone.slice(0, zone.indexOf("/")) : "Other";
}

/**
 * Every zone, grouped by region, with the few worth reaching first pinned above.
 *
 * Four hundred options in one flat list is a scroll, so the regions are
 * `optgroup`s -- which native selects also let people type-ahead within. The
 * pinned group is UTC (the default), the browser's own zone, and whatever this
 * schedule is already set to.
 *
 * `current` is what keeps this safe to edit with. Any IANA zone is accepted by
 * the API, and a zone with no matching `<option>` makes a select show its first
 * entry instead -- so saving an untouched form would move the job to UTC. That
 * is a silent reschedule rather than an edit, and it cannot happen if the value
 * is always present.
 */
export function timezoneGroups(current?: string | null, at: Date = new Date()): TimezoneGroup[] {
  const local = localTimezone();
  const label = (zone: string, suffix?: string) => ({
    value: zone,
    label: `${cityOf(zone)} (${offsetLabel(zone, at)})${suffix ? ` — ${suffix}` : ""}`,
  });

  const t = i18next.getFixedT(null, "schedules");
  const pinned: TimezoneOption[] = [{ value: DEFAULT_TIMEZONE, label: "UTC" }];
  if (local) pinned.push(label(local, t("timezone.yours")));
  if (current && current !== DEFAULT_TIMEZONE && current !== local) {
    pinned.push(label(current, t("timezone.current")));
  }

  const byRegion = new Map<string, TimezoneOption[]>();
  for (const zone of allTimezones()) {
    if (zone === DEFAULT_TIMEZONE) continue;
    const region = regionOf(zone);
    const options = byRegion.get(region) ?? [];
    options.push(label(zone));
    byRegion.set(region, options);
  }

  return [
    { label: t("timezone.common"), options: pinned },
    ...[...byRegion.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([region, zones]) => ({
        label: region,
        options: zones.sort((a, b) => a.label.localeCompare(b.label)),
      })),
  ];
}
